import {
  AssessmentOutlined,
  CameraOutlined,
  Description,
  ElectricBolt,
  Extension,
  Folder,
  FolderShared,
  ImageAspectRatio,
  InsertChart,
  InsertDriveFileOutlined,
  MonetizationOn,
} from "@mui/icons-material";
import { useTheme } from "@mui/material";

import { ResourceEntity } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";

type EntityUtil = {
  getEntityIcon: (entity?: ResourceEntity) => JSX.Element;
  getEntityName: (entity?: ResourceEntity) => string | undefined;
  getEntityLabel: (entity?: ResourceEntity, labelLength?: "short" | "long") => string;
  getEntityUrl: (entity?: ResourceEntity) => string;
};

export const useEntityUtil = (): EntityUtil => {
  const theme = useTheme();
  const currentUsername = useAppSelector((state) => state.cognito.user?.username);

  const getEntityIcon = (entity?: ResourceEntity): JSX.Element => {
    if (!entity) return <span />;

    const { EntityType } = entity;

    const creatorEmail = EntityType === "report" ? entity?.Creator.Email : entity?.Creator;

    if (EntityType === "folder" && entity.MultiProject) return <CameraOutlined color="primary" />;
    if (EntityType === "folder" && entity.MultiReport) return <CameraOutlined color="tertiary" />;
    if (EntityType === "folder" && creatorEmail !== currentUsername) return <FolderShared color="secondary" />;
    if (EntityType === "folder") return <Folder color="secondary" />;
    if (EntityType === "report" && entity.Type === "financial_analytics_imported") return <AssessmentOutlined color="tertiary" />;
    if (EntityType === "report") return <InsertChart color="tertiary" />;
    if (EntityType === "project" && entity.Type === "planner") {
      if (entity.Subtype === "planner_extended") return <ImageAspectRatio color="primary" />;
      return <Description color="primary" />;
    }
    if (EntityType === "project" && entity.Type === "collaboration")
      return <InsertDriveFileOutlined sx={{ color: theme.palette.biarri.secondary.cyan }} />;
    if (EntityType === "project" && entity.Type === "design") {
      if (entity.Subtype === "nbn_cost_to_serve") return <MonetizationOn color="primary" />;
      if (entity.Subtype === "nbn_idd_lite") return <Extension color="primary" />;
      if (entity.Subtype === "mu_power") return <ElectricBolt color="primary" />;
      return <ImageAspectRatio color="primary" />;
    }

    return <span />;
  };

  const getEntityUrl = (entity?: ResourceEntity): string => {
    if (!entity) return "";
    const { EntityType, ID } = entity;

    if (EntityType === "folder" && entity.MultiProject) return `/cities/${ID}`;
    if (EntityType === "folder") return `/folders/${ID}`;
    if (EntityType === "project") return `/project/${ID}`;
    if (EntityType === "report") return `/reports/${ID}`;

    return "";
  };

  const getEntityName = (entity?: ResourceEntity): string | undefined => {
    if (entity?.EntityType === "project") {
      return entity.ProjectName;
    }
    return entity?.Name;
  };

  const entityTypeLabel = {
    MULTI_PROJECT: { short: "Market Lens", long: "Market Lens" },
    MULTI_REPORT: { short: "Market Lens Report", long: "Market Lens Report" },
    FOLDER: { short: "Folder", long: "Folder" },
    SERVICES_REPORT: { short: "Services Report", long: "Services Report" },
    CITY_SUBAREA_REPORT: { short: "City Subarea Report", long: "City Subarea Report" },
    REPORT: { short: "Report", long: "Report" },
    PLANNER_PROJECT: { short: "Planner", long: "Planner Project" },
    COLLABORATION_PROJECT: { short: "Collaboration", long: "Collaboration Project" },
    DESIGN_PROJECT: { short: "Design", long: "Design Project" },
  } as const;

  const getEntityLabel = (entity?: ResourceEntity, labelLength: "short" | "long" = "long"): string => {
    if (!entity) return "-";

    const { EntityType } = entity;

    if (EntityType === "folder" && entity.MultiProject) return entityTypeLabel.MULTI_PROJECT[labelLength];
    if (EntityType === "folder" && entity.MultiReport) return entityTypeLabel.MULTI_REPORT[labelLength];
    if (EntityType === "folder") return entityTypeLabel.FOLDER[labelLength];
    if (EntityType === "report" && entity.Type === "financial_analytics_imported") return entityTypeLabel.SERVICES_REPORT[labelLength];
    if (EntityType === "report" && entity.MultiReport) return entityTypeLabel.CITY_SUBAREA_REPORT[labelLength];
    if (EntityType === "report") return entityTypeLabel.REPORT[labelLength];
    if (EntityType === "project" && entity.Type === "planner") {
      if (entity.Subtype === "planner_extended") return entityTypeLabel.DESIGN_PROJECT[labelLength];
      return entityTypeLabel.PLANNER_PROJECT[labelLength];
    }
    if (EntityType === "project" && entity.Type === "collaboration") return entityTypeLabel.COLLABORATION_PROJECT[labelLength];
    if (EntityType === "project" && entity.Type === "design") return entityTypeLabel.DESIGN_PROJECT[labelLength];

    return "-";
  };

  return {
    getEntityIcon,
    getEntityName,
    getEntityLabel,
    getEntityUrl,
  };
};
